import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { TrainingService } from "services/training.service";
import { ISaveTrainingDTO } from "models/church.types";

export default function TrainingRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; startDate?: string }>(
    {},
  );

  // Initialisation conforme à l'interface ISaveTrainingDTO
  const [training, setTraining] = useState<ISaveTrainingDTO>({
    title: "",
    description: "",
    startDate: new Date().toISOString(),
    endDate: "",
    instructor: "",
  });

  useEffect(() => {
    const fetchTraining = async () => {
      if (isEditMode && id) {
        try {
          const data = await TrainingService.getById(parseInt(id));
          setTraining({
            title: data.title,
            description: data.description || "",
            startDate: data.startDate,
            endDate: data.endDate || "",
            instructor: data.instructor || "",
          });
        } catch (error) {
          toast.error("Erreur lors du chargement de la formation");
          history.push("/admin/trainings");
        }
      }
    };
    fetchTraining();
  }, [id, isEditMode, history]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation des contraintes @NotNull du Backend
    const newErrors: any = {};
    if (!training.title || !training.title.trim()) {
      newErrors.title = "Le titre de la formation est obligatoire";
    }
    if (!training.startDate) {
      newErrors.startDate = "La date de début est obligatoire";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      // Nettoyage de la date de fin si elle reste vide
      const payload: ISaveTrainingDTO = {
        ...training,
        endDate: training.endDate ? training.endDate : undefined,
      };

      if (isEditMode) {
        // Envoi via le DTO UpdateTrainingDTO (avec l'ID)
        await TrainingService.update({ ...payload, id: parseInt(id) });
        toast.success("Formation mise à jour avec succès");
      } else {
        await TrainingService.create(payload);
        toast.success("Formation enregistrée avec succès");
      }
      history.push("/admin/trainings");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  // Sécurise le découpage pour l'affichage de datetime-local (YYYY-MM-DDTHH:mm)
  const formatToLocalInputValue = (isoString: string) => {
    if (!isoString) return "";
    return isoString.slice(0, 16);
  };

  return (
    /* CORRECTION : Remplacement de mt-12 et justify-center par un comportement adaptatif */
    <div className="flex flex-col items-center justify-start md:justify-center mt-14 p-4 w-full min-h-screen py-12 md:py-6">
      <div className="w-full max-w-2xl">
        <div className="bg-[#1E293B] border border-slate-700 shadow-2xl rounded-xl overflow-hidden">
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 flex justify-between items-center">
            <h6 className="text-white text-sm font-bold uppercase tracking-widest">
              <i className="fas fa-graduation-cap mr-2 text-[#D4AF37]"></i>
              {isEditMode ? "Éditer la formation" : "Nouvelle formation"}
            </h6>
          </div>

          <form
            onSubmit={handleSubmit}
            className="px-6 py-8 grid grid-cols-1 gap-6"
          >
            {/* Titre de la formation */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Titre de la formation *
              </label>
              <input
                type="text"
                value={training.title}
                onChange={(e) => {
                  setTraining({ ...training, title: e.target.value });
                  if (errors.title)
                    setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder="Ex: Formation des Disciples, Classe de Baptême..."
                className={`w-full bg-[#0F172A] border ${
                  errors.title ? "border-red-500" : "border-slate-700"
                } rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all`}
              />
              {errors.title && (
                <span className="text-red-500 text-xs mt-1 ml-1 block">
                  {errors.title}
                </span>
              )}
            </div>

            {/* Instructeur / Orateur */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Instructeur / Formateur
              </label>
              <input
                type="text"
                value={training.instructor}
                onChange={(e) =>
                  setTraining({ ...training, instructor: e.target.value })
                }
                placeholder="Nom du formateur ..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all"
              />
            </div>

            {/* Dates (Début et Fin en ligne sur desktop) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date de début */}
              <div>
                <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                  Date & Heure de début *
                </label>
                <input
                  type="datetime-local"
                  value={formatToLocalInputValue(training.startDate)}
                  onChange={(e) => {
                    setTraining({
                      ...training,
                      startDate: e.target.value
                        ? new Date(e.target.value).toISOString()
                        : "",
                    });
                    if (errors.startDate)
                      setErrors((prev) => ({ ...prev, startDate: undefined }));
                  }}
                  className={`w-full bg-[#0F172A] border ${
                    errors.startDate ? "border-red-500" : "border-slate-700"
                  } rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all`}
                />
                {errors.startDate && (
                  <span className="text-red-500 text-xs mt-1 ml-1 block">
                    {errors.startDate}
                  </span>
                )}
              </div>

              {/* Date de fin */}
              <div>
                <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                  Date & Heure de fin
                </label>
                <input
                  type="datetime-local"
                  value={formatToLocalInputValue(training.endDate || "")}
                  onChange={(e) =>
                    setTraining({
                      ...training,
                      endDate: e.target.value
                        ? new Date(e.target.value).toISOString()
                        : "",
                    })
                  }
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Description du programme / Objectifs
              </label>
              <textarea
                value={training.description || ""}
                onChange={(e) =>
                  setTraining({ ...training, description: e.target.value })
                }
                placeholder="Détails de la session, plan de cours ou matériel requis..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none h-28 transition-all resize-none"
              />
            </div>

            {/* Boutons d'action */}
            <div className="mt-6 flex justify-end gap-4 border-t border-slate-700 pt-6">
              <button
                type="button"
                onClick={() => history.push("/admin/trainings")}
                className="px-6 py-3 text-slate-400 hover:text-white font-bold text-xs uppercase transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase hover:scale-105 active:scale-95 transition-all shadow-md disabled:opacity-50"
              >
                {loading ? "Chargement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
